import type { ColumnConfig } from "../types";
import type { TableEventType, TableEventContext, TableEventFn } from "./types";

export type { TableEventType, TableEventContext, TableEventFn };

// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------
const handlers: Record<string, TableEventFn[]> = {};

export function registerTableEvent(
  modelLabel: string,
  eventType: TableEventType,
  handler: TableEventFn,
): void {
  const key = `${modelLabel}.${eventType}`;
  if (!handlers[key]) handlers[key] = [];
  handlers[key].push(handler);
}

export function fireTableEvent(
  modelLabel: string,
  ctx: TableEventContext,
): void {
  const fns = handlers[`${modelLabel}.${ctx.type}`];
  if (fns) fns.forEach((fn) => fn(ctx));
}

function hasHandlers(modelLabel: string, eventType: string): boolean {
  const fns = handlers[`${modelLabel}.${eventType}`];
  return !!fns && fns.length > 0;
}

function hasAnyPrefixed(modelLabel: string, prefix: string): boolean {
  const p = `${modelLabel}.${prefix}`;
  return Object.keys(handlers).some((k) => k.startsWith(p) && handlers[k].length > 0);
}

// ---------------------------------------------------------------------------
// Event type → React prop mapping
// ---------------------------------------------------------------------------
const rowEventMap: Record<string, string> = {
  row_click: "onClick",
  row_dblclick: "onDoubleClick",
  row_contextmenu: "onContextMenu",
};

const cellEventMap: Record<string, string> = {
  cell_click: "onClick",
  cell_dblclick: "onDoubleClick",
  cell_contextmenu: "onContextMenu",
};

// ---------------------------------------------------------------------------
// Props builders
// ---------------------------------------------------------------------------
export function getRowEventProps(
  modelLabel: string,
  row: Record<string, any>,
): Record<string, (e: React.MouseEvent) => void> {
  const props: Record<string, (e: React.MouseEvent) => void> = {};
  for (const [eventType, reactProp] of Object.entries(rowEventMap)) {
    if (hasHandlers(modelLabel, eventType)) {
      props[reactProp] = (e) =>
        fireTableEvent(modelLabel, { type: eventType, row, originalEvent: e });
    }
  }
  return props;
}

export function getCellEventProps(
  modelLabel: string,
  row: Record<string, any>,
  column: ColumnConfig,
): Record<string, (e: React.MouseEvent) => void> {
  const props: Record<string, (e: React.MouseEvent) => void> = {};
  for (const [eventType, reactProp] of Object.entries(cellEventMap)) {
    if (hasHandlers(modelLabel, eventType)) {
      props[reactProp] = (e) =>
        fireTableEvent(modelLabel, {
          type: eventType,
          row,
          column,
          value: row[column.id],
          originalEvent: e,
        });
    }
  }
  return props;
}

export function hasCellEvents(modelLabel: string): boolean {
  return hasAnyPrefixed(modelLabel, "cell_");
}
